import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  FormGroup,
  FormControlLabel,
  useTheme
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../../config/api';
import { MODULES, MODULE_GROUPS } from '../../config/modules';
import { pagesForModule } from '../../config/navigation';
import { useAuth } from '../../context/AuthContext';

const SUPER_ADMIN_COMPANY_ID = 1;

const PermissionsDialog = ({ open, onClose, user, teamId, onPermissionsUpdated, reloadError = '' }) => {
  const theme = useTheme();
  const { user: adminUser, permissions: adminPermissions } = useAuth();

  // Authority gates: what is the CURRENT ADMIN allowed to grant?
  // Super-admin (Excite company_id=1) can grant everything. Non-super-admins
  // can only grant what they themselves have (matches backend defense at
  // updateUserPermissions / updateUserDataAccess).
  const isSuperAdmin = !!adminUser
    && Number(adminUser.company_id) === SUPER_ADMIN_COMPANY_ID
    && !!adminUser.isCompanyAdmin;
  const adminPerms = Array.isArray(adminPermissions) ? adminPermissions : [];
  const canGrantModule = (key) => isSuperAdmin || adminPerms.includes(key);
  const canGrantAIAgents = canGrantModule(MODULES.EXPERTS);
  const canGrantRawData = isSuperAdmin || !!adminUser?.rawDataAccess;
  const canGrantAmazonSearch = isSuperAdmin || !!adminUser?.amazonSearchAccess;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Current Slack permission state from server
  const [permissions, setPermissions] = useState(null);

  // Available resources
  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);

  // DT-2017: a failed load of the user's current grants is shown, and its section stays hidden (never marked
  // loaded), so a save can never write a guessed state (all sections, or one brand / agent instead of the list).
  const [loadErrors, setLoadErrors] = useState({});
  const setLoadError = (key, message) => setLoadErrors(prev => ({ ...prev, [key]: message }));
  const RETRY = 'Close and reopen this dialog to try again.';

  // Whether this user has a Slack identity
  const isSlackUser = !!(user?.slack_user_id);

  useEffect(() => {
    if (open && user) {
      setError('');
      setSuccess('');
      if (isSlackUser) {
        fetchPermissions();
      } else {
        // Web-only user — no Slack permission mode to load
        setPermissions({ permissionMode: null });
      }
      fetchAvailableResources();
    }
  }, [open, user]);

  const fetchPermissions = async () => {
    if (!isSlackUser) return;
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/slack/admin/user-permissions/${user.slack_user_id}/${teamId}`);
      if (response.data.success) {
        setPermissions(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableResources = async () => {
    setLoadError('resources', '');
    try {
      const promises = [api.get('/slack/admin/available-agents')];
      if (teamId) {
        promises.unshift(api.get(`/slack/admin/available-brands/${teamId}`));
      } else {
        // Fallback: load brands from brand service
        promises.unshift(api.get('/api/brands'));
      }
      const [brandsRes, agentsRes] = await Promise.all(promises);
      if (teamId && brandsRes.data.success) {
        setAvailableBrands(brandsRes.data.data);
      } else if (!teamId) {
        // /api/brands returns array directly
        setAvailableBrands(brandsRes.data || []);
      }
      if (agentsRes.data.success) setAvailableAgents(agentsRes.data.data);
    } catch (err) {
      console.error('Error fetching available resources:', err);
      setLoadError('resources', `Could not load the list of brands, so brand access is not shown and cannot be changed. ${RETRY}`);
    }
  };

  const handlePermissionModeChange = async (event) => {
    if (!isSlackUser) return;
    const newMode = event.target.value;
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await api.post('/slack/admin/update-permission-mode', {
        slackUserId: user.slack_user_id,
        slackTeamId: teamId,
        permissionMode: newMode
      });
      setSuccess('Permission mode updated');
      await fetchPermissions();
      if (onPermissionsUpdated) onPermissionsUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permission mode');
    } finally {
      setLoading(false);
    }
  };

  // Brand toggle — uses platform API (user_brand_access, single source of truth)
  const handleToggleBrand = async (brandId, isCurrentlyGranted) => {
    if (!resolvedPlatformUserId || !platformBrandsLoaded || loadErrors.resources) return;
    const newIds = isCurrentlyGranted
      ? platformUserBrandIds.filter(id => id !== brandId)
      : [...platformUserBrandIds, brandId];
    // If removing the default brand, clear default
    const newDefault = (brandId === platformDefaultBrandId && isCurrentlyGranted)
      ? null : platformDefaultBrandId;
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await api.put(`/api/mcp-admin/users/${resolvedPlatformUserId}/brands`, {
        brand_ids: newIds,
        default_brand_id: newDefault
      });
      setPlatformUserBrandIds(newIds);
      setPlatformDefaultBrandId(newDefault);
      setSuccess('Brand access updated');
      if (onPermissionsUpdated) onPermissionsUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update brand access');
    } finally {
      setLoading(false);
    }
  };

  // Set default brand — uses platform API
  const handleSetDefaultBrand = async (brandId) => {
    if (!resolvedPlatformUserId || !platformBrandsLoaded) return;
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await api.put(`/api/mcp-admin/users/${resolvedPlatformUserId}/brands/default`, {
        brand_id: brandId
      });
      setPlatformDefaultBrandId(brandId);
      setSuccess('Default brand updated');
      if (onPermissionsUpdated) onPermissionsUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set default brand');
    } finally {
      setLoading(false);
    }
  };

  // Resolved platform user ID (may differ from user.scm_user_id if auto-provisioned)
  const [resolvedPlatformUserId, setResolvedPlatformUserId] = useState(null);

  // Platform-level brand access (single source of truth)
  const [platformUserBrandIds, setPlatformUserBrandIds] = useState([]); // user's assigned brand IDs
  const [platformDefaultBrandId, setPlatformDefaultBrandId] = useState(null);
  const [platformBrandsLoaded, setPlatformBrandsLoaded] = useState(false);

  // Platform-level agent access (always visible, independent of Slack permission mode)
  const [platformAgents, setPlatformAgents] = useState([]); // all available agents
  const [platformUserAgentIds, setPlatformUserAgentIds] = useState([]); // user's assigned agent IDs
  const [platformAgentsLoaded, setPlatformAgentsLoaded] = useState(false);

  // Data access flags (platform-level, not Slack-specific)
  const [rawDataAccess, setRawDataAccess] = useState(false);
  const [amazonSearchAccess, setAmazonSearchAccess] = useState(false);
  const [dataAccessLoaded, setDataAccessLoaded] = useState(false);

  // Module/section permissions (platform-level)
  const [modulePermissions, setModulePermissions] = useState([]);
  const [modulePermissionsLoaded, setModulePermissionsLoaded] = useState(false);

  // Step 1: Resolve platform user ID (auto-provision if needed)
  useEffect(() => {
    if (!open || !user) return;
    // DT-2017: ignore a late answer for a user whose dialog was closed or replaced.
    let stale = false;
    setResolvedPlatformUserId(null);
    setLoadErrors(prev => ({ ...prev, provision: '', user: '', brands: '', agents: '' }));

    if (user.scm_user_id > 0) {
      // Already has a platform account
      setResolvedPlatformUserId(user.scm_user_id);
    } else if (user.slack_email) {
      // No platform account — auto-provision via ensurePlatformUser
      api.post('/api/mcp-admin/ensure-platform-user', {
        email: user.slack_email,
        fullName: user.slack_display_name || ''
        // companyId intentionally omitted — the server derives the tenant from the admin's JWT
      })
        .then(res => {
          if (stale) return;
          const newId = res.data.user_id;
          setResolvedPlatformUserId(newId);
          // Update the user object so parent knows the new ID
          user.scm_user_id = newId;
        })
        .catch(err => {
          console.error('Error provisioning platform user:', err);
          if (stale) return;
          setLoadError('provision', `Could not set up a platform account for this user, so their brand, agent, data and section access is not shown and cannot be changed. ${RETRY}`);
        });
    }
    return () => { stale = true; };
  }, [open, user?.scm_user_id, user?.slack_email]);

  // Step 2: Load platform-level data once we have a resolved platform user ID
  useEffect(() => {
    if (!open || !resolvedPlatformUserId) return;
    // DT-2017: a late answer for the previously opened user must not fill (and mark loaded) this user's sections.
    let stale = false;

    setDataAccessLoaded(false);
    setPlatformAgentsLoaded(false);
    setPlatformBrandsLoaded(false);
    setModulePermissionsLoaded(false);
    setLoadErrors(prev => ({ ...prev, user: '', brands: '', agents: '' }));

    // Load data access flags + module permissions
    api.get(`/api/users/${resolvedPlatformUserId}`)
      .then(res => {
        if (stale) return;
        setRawDataAccess(Number(res.data.raw_data_access) === 1);
        setAmazonSearchAccess(Number(res.data.amazon_search_access) === 1);
        setDataAccessLoaded(true);

        // Parse module permissions
        let perms = [];
        try {
          perms = res.data.permissions ? JSON.parse(res.data.permissions) : [];
        } catch (e) {
          perms = [];
        }
        // Empty array = legacy full access — default to all modules
        if (perms.length === 0) {
          setModulePermissions(Object.values(MODULES));
        } else {
          setModulePermissions(perms);
        }
        setModulePermissionsLoaded(true);
      })
      .catch(err => {
        console.error('Error fetching user data:', err);
        if (stale) return;
        setLoadError('user', `Could not load this user's web section access and data access, so they are not shown and cannot be changed. ${RETRY}`);
      });

    // Load platform brands (user's assigned brands)
    api.get(`/api/mcp-admin/users/${resolvedPlatformUserId}/brands`)
      .then(res => {
        if (stale) return;
        const brands = res.data || [];
        setPlatformUserBrandIds(brands.map(b => b.brand_id));
        const def = brands.find(b => b.is_default === 1);
        setPlatformDefaultBrandId(def ? def.brand_id : null);
        setPlatformBrandsLoaded(true);
      })
      .catch(err => {
        console.error('Error fetching platform brands:', err);
        if (stale) return;
        setLoadError('brands', `Could not load this user's brand access, so it is not shown and cannot be changed. ${RETRY}`);
      });

    // Load platform agents + user's assigned agents
    Promise.all([
      api.get('/api/mcp-admin/agents'),
      api.get(`/api/mcp-admin/users/${resolvedPlatformUserId}/agents`)
    ])
      .then(([allRes, userRes]) => {
        if (stale) return;
        setPlatformAgents(allRes.data || []);
        setPlatformUserAgentIds((userRes.data || []).map(a => a.agent_id || a.id));
        setPlatformAgentsLoaded(true);
      })
      .catch(err => {
        console.error('Error fetching platform agents:', err);
        if (stale) return;
        setLoadError('agents', `Could not load this user's AI agent access, so it is not shown and cannot be changed. ${RETRY}`);
      });
    return () => { stale = true; };
  }, [open, resolvedPlatformUserId]);

  const handleTogglePlatformAgent = async (agentId) => {
    if (!resolvedPlatformUserId || !platformAgentsLoaded) return;
    const newIds = platformUserAgentIds.includes(agentId)
      ? platformUserAgentIds.filter(id => id !== agentId)
      : [...platformUserAgentIds, agentId];
    try {
      setLoading(true);
      setError('');
      await api.put(`/api/mcp-admin/users/${resolvedPlatformUserId}/agents`, {
        agent_ids: newIds
      });
      setPlatformUserAgentIds(newIds);
      setSuccess('Agent access updated');
      if (onPermissionsUpdated) onPermissionsUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update agent access');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDataAccess = async (field, currentValue) => {
    if (!resolvedPlatformUserId || !dataAccessLoaded) return;
    const newRaw = field === 'raw' ? !currentValue : rawDataAccess;
    const newAmazon = field === 'amazon' ? !currentValue : amazonSearchAccess;
    try {
      setLoading(true);
      setError('');
      await api.put(`/api/users/${resolvedPlatformUserId}/data-access`, {
        rawDataAccess: newRaw,
        amazonSearchAccess: newAmazon
      });
      setRawDataAccess(newRaw);
      setAmazonSearchAccess(newAmazon);
      setSuccess('Data access updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update data access');
    } finally {
      setLoading(false);
    }
  };

  // Module permission handlers
  const handleToggleModule = (moduleKey) => {
    setModulePermissions(prev => {
      if (prev.includes(moduleKey)) {
        return prev.filter(key => key !== moduleKey);
      } else {
        return [...prev, moduleKey];
      }
    });
  };

  const handleToggleAllModules = () => {
    const allModules = Object.values(MODULES);
    if (modulePermissions.length === allModules.length) {
      setModulePermissions([]);
    } else {
      setModulePermissions(allModules);
    }
  };

  const handleSaveModulePermissions = async () => {
    if (!resolvedPlatformUserId || !modulePermissionsLoaded) return;
    try {
      setLoading(true);
      setError('');
      await api.put(`/api/users/${resolvedPlatformUserId}/permissions`, {
        permissions: modulePermissions
      });
      setSuccess('Web section permissions saved');
      if (onPermissionsUpdated) onPermissionsUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save section permissions');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  // Display name for the dialog subtitle
  const displayName = user.slack_display_name || user.display_name || user.slack_email || user.email || '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Manage Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {displayName}
            </Typography>
          </Box>
          {isSlackUser && (
            <IconButton onClick={fetchPermissions} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        {/* Alerts */}
        {error && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
          </Box>
        )}
        {success && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
          </Box>
        )}
        {/* DT-2012: the parent's list reload (onPermissionsUpdated) failed; its page message is behind this dialog. */}
        {reloadError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="warning">{reloadError}</Alert>
          </Box>
        )}
        {/* DT-2017: a failed load of the user's current grants; that section stays hidden. */}
        {['provision', 'resources', 'user', 'brands', 'agents']
          .filter(key => loadErrors[key] && (key !== 'agents' || canGrantAIAgents))
          .map(key => (
            <Box key={key} sx={{ px: 3, pt: 2 }}>
              <Alert severity="error">{loadErrors[key]}</Alert>
            </Box>
          ))}

        {loading && !permissions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ px: 3, py: 2 }}>
            {/* Permission Mode — Slack users only */}
            {isSlackUser && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Slack Permission Mode
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 0.5 }}>
                  <Select
                    value={permissions?.permissionMode || 'restricted'}
                    onChange={handlePermissionModeChange}
                    disabled={loading}
                  >
                    <MenuItem value="full_access">Full Access</MenuItem>
                    <MenuItem value="restricted">Restricted</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                  {permissions?.permissionMode === 'admin' && 'Full access + can manage other users via /scm-admin'}
                  {permissions?.permissionMode === 'full_access' && 'Can access all brands, agents, and templates'}
                  {permissions?.permissionMode === 'restricted' && 'Can only access brands and agents checked below'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            {/* Brand Access — uses platform API (user_brand_access, single source of truth) */}
            {resolvedPlatformUserId > 0 && platformBrandsLoaded && !loadErrors.resources && (
            <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Brand Access
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({platformUserBrandIds.length} of {availableBrands.length})
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Check brands this user can access. Click the star to set their default brand.
            </Typography>
            <FormGroup>
              <Grid container>
                {availableBrands.map(brand => {
                  const isGranted = platformUserBrandIds.includes(brand.id);
                  const isDefault = platformDefaultBrandId === brand.id;
                  return (
                    <Grid item xs={6} key={brand.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isGranted}
                              onChange={() => handleToggleBrand(brand.id, isGranted)}
                              disabled={loading}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: isDefault ? 600 : 400 }}>
                              {brand.name}
                            </Typography>
                          }
                          sx={{ mr: 0 }}
                        />
                        {isGranted && (
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefaultBrand(brand.id)}
                            disabled={loading}
                            sx={{ ml: -0.5, p: 0.25 }}
                            title={isDefault ? 'Default brand' : 'Set as default'}
                          >
                            {isDefault ? (
                              <StarIcon sx={{ fontSize: 16, color: '#FFB400' }} />
                            ) : (
                              <StarBorderIcon sx={{ fontSize: 16, color: theme.palette.grey[400] }} />
                            )}
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </FormGroup>
            </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* AI Agent Access — hidden if the current admin can't grant AI Agents themselves */}
            {canGrantAIAgents && resolvedPlatformUserId > 0 && platformAgentsLoaded && platformAgents.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  AI Agent Access
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({platformUserAgentIds.length} of {platformAgents.length})
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Check which AI agents this user can chat with.
                </Typography>
                <FormGroup>
                  <Grid container>
                    {platformAgents.map(agent => {
                      const isGranted = platformUserAgentIds.includes(agent.id);
                      return (
                        <Grid item xs={6} key={agent.id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isGranted}
                                onChange={() => handleTogglePlatformAgent(agent.id)}
                                disabled={loading}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">{agent.name}</Typography>
                            }
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </FormGroup>
              </>
            )}

            {/* Data Access — section hidden entirely if admin can grant neither flag */}
            {(canGrantRawData || canGrantAmazonSearch) && resolvedPlatformUserId > 0 && dataAccessLoaded && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Data Access
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Controls access to raw source databases via MCP/chat. Applies regardless of permission mode.
                </Typography>
                <FormGroup>
                  <Grid container>
                    <Grid item xs={6} sx={!canGrantRawData ? { display: 'none' } : undefined}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rawDataAccess}
                            onChange={() => handleToggleDataAccess('raw', rawDataAccess)}
                            disabled={loading}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Raw Source Databases</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Meta, Google Ads, TikTok, Shopify, etc.
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sx={!canGrantAmazonSearch ? { display: 'none' } : undefined}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={amazonSearchAccess}
                            onChange={() => handleToggleDataAccess('amazon', amazonSearchAccess)}
                            disabled={loading}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">Amazon Search Terms</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Brand Analytics, search rankings
                            </Typography>
                          </Box>
                        }
                      />
                    </Grid>
                  </Grid>
                </FormGroup>
              </>
            )}

            {/* Web Section Permissions — platform-level, controls sidebar visibility */}
            {resolvedPlatformUserId > 0 && modulePermissionsLoaded && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Web Section Access
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleToggleAllModules}
                      disabled={loading}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {modulePermissions.length === Object.values(MODULES).length ? 'Remove All' : 'Grant All'}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveModulePermissions}
                      disabled={loading}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Save Sections
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Controls which sections are visible in the web sidebar. Currently: {modulePermissions.length === Object.values(MODULES).length ? 'Full Access' : modulePermissions.length === 0 ? 'No Access' : `${modulePermissions.length} section(s)`}
                </Typography>

                {/* Rendered from MODULE_GROUPS (config/modules.js). The caption under each
                    checkbox lists the sidebar pages it unlocks, derived from config/navigation.js —
                    new pages added there show up here automatically. */}
                {MODULE_GROUPS.map(group => {
                  // Hide the whole group (header included) when the viewing admin
                  // can't grant any module in it — an empty header reads as a bug.
                  if (!group.modules.some(({ key }) => canGrantModule(key))) return null;
                  return (
                  <React.Fragment key={group.label}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mt: 1, display: 'block' }}>
                      {group.label}
                    </Typography>
                    <FormGroup>
                      <Grid container>
                        {group.modules.map(({ key, label }) => {
                          const pages = pagesForModule(key);
                          return (
                            <Grid item xs={4} key={key} sx={!canGrantModule(key) ? { display: 'none' } : undefined}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={modulePermissions.includes(key)}
                                    onChange={() => handleToggleModule(key)}
                                    disabled={loading}
                                    size="small"
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2">{label}</Typography>
                                    {pages.length > 1 && (
                                      <Typography variant="caption" color="text.secondary">
                                        {pages.join(', ')}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </FormGroup>
                  </React.Fragment>
                  );
                })}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsDialog;
